// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: words Agents shows in more than one place: error messages for the API's agent error
// codes, the short list times, "Offline since 13:10", what Konvo may do on a machine, the line
// under each thread (what it waits on, why it failed or paused on its plan, how long it worked),
// how long a turn ran, and plan usage times.

import {checksTally, pullPhase} from '@app/features/konvo_agents/state/AgentPulls';
import {
	agentErrorCode,
	durationSeconds,
	endedAt,
	isBackgroundWorking,
	type ShortAgo,
	shortAgo,
} from '@app/features/konvo_agents/state/AgentsModel';
import {
	type AgentLimits,
	type AgentPullStatus,
	type AgentSwitchProblem,
	AgentSwitchProblemSchema,
	type AgentThreadSummary,
} from '@fluxer/schema/src/domains/konvo/Agents';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

const ASK_CLOSED_ERROR = msg({
	message: 'This question is no longer open. It was answered, or the agent moved on.',
	comment: "Agents: error when answering an agent's question someone sent you, after it closed.",
});
const QUESTION_CHANGED_ERROR = msg({
	message: 'The agent changed its question. Look again before you answer.',
	comment: "Agents: error when answering an agent's question someone sent you, after the agent reworded it.",
});
const ASK_NOT_ALLOWED_ERROR = msg({
	message: "You can't ask that person.",
	comment: "Agents: error when sending the agent's question to someone you can't message.",
});
const OFFLINE_ERROR = msg({
	message: 'That machine is offline. Try again when it is back.',
	comment: 'Agents: error when the machine running the coding agents is not connected to Konvo.',
});
const READ_ONLY_ERROR = msg({
	message: 'This machine shares its chats for reading only.',
	comment: 'Agents: error when sending to a machine whose owner only allows reading.',
});
const APPROVALS_OFF_ERROR = msg({
	message:
		'Allow and Deny from Konvo are off for this machine. Its owner turns them on in Settings → Connections → Agents.',
	comment: 'Agents: error when answering an approval while the machine’s Allow and Deny from Konvo switch is off.',
});
const PROJECT_HIDDEN_ERROR = msg({
	message: "That project isn't shared with Konvo.",
	comment: "Agents: error when a project is not in the machine owner's shared list.",
});
const TOOL_DOWN_ERROR = msg({
	message: "The coding app on that computer isn't answering right now. Try again in a moment.",
	comment:
		'Agents: error when the machine is connected to Konvo but its coding app (T3 Code) is not running or did not answer.',
});
const TIMEOUT_ERROR = msg({
	message: "The machine didn't answer in time. Try again.",
	comment: 'Agents: error when the machine took too long to answer.',
});
const NOT_FOUND_ERROR = msg({
	message: "Konvo couldn't find that on the machine.",
	comment: 'Agents: error when a thread or machine no longer exists.',
});
const NOT_ENABLED_ERROR = msg({
	message: "Agents isn't switched on for you yet.",
	comment: 'Agents: error when the per-person Agents switch is off.',
});
const GENERIC_ERROR = msg({
	message: 'Something went wrong. Try again.',
	comment: 'Agents: generic error.',
});

const GITHUB_NOT_CONNECTED_ERROR = msg({
	message: 'Connect GitHub in Settings → Assistant first.',
	comment: 'Agents: error when a pull request action needs the person’s GitHub connection.',
});
const MERGE_NOT_ALLOWED_ERROR = msg({
	message: "You can't merge in this repository from Konvo. Ask someone who can.",
	comment: 'Agents: error when the person is not on the merge rights for the repository.',
});
const NO_MERGERS_ERROR = msg({
	message: 'Nobody can merge in this repository from Konvo yet. Staff choose who in Settings → Connections → Agents.',
	comment: 'Agents: error for Ask to merge when no one has merge rights for the repository.',
});
const MERGE_REFUSED_ERROR = msg({
	message: 'Not merged: {reason}',
	comment: 'Agents: error when GitHub refused a merge; {reason} is GitHub’s or Konvo’s explanation.',
});

const SWITCH_FAILED_ERROR = msg({
	message:
		"Couldn't switch the checkout to that branch. Commit or stash its changes first, or start in a new worktree.",
	comment: 'Agents: a new chat could not switch the project checkout to the chosen branch (git refused).',
});
const CHECKOUT_BUSY_ERROR = msg({
	message:
		'An agent is working in this checkout, so its changes stay put. Start in a new worktree, or wait until it finishes.',
	comment:
		'Agents: putting a checkout’s changes aside (git stash) was refused because another agent chat is working in it.',
});
const STASH_FAILED_ERROR = msg({
	message: "Couldn't put the changes aside (git stash refused), so nothing was started.",
	comment: 'Agents: git stash failed before a new chat could switch the checkout to its branch.',
});

function errorBody(error: unknown): Record<string, unknown> | null {
	const body = error && typeof error === 'object' ? (error as {body?: unknown}).body : null;
	return body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
}

/** The API's own explanation, when it sent one. */
function serverMessage(error: unknown): string {
	const message = errorBody(error)?.message;
	return typeof message === 'string' ? message : '';
}

/** The machine's own words for a failure (untranslated), for details and copies. */
export function agentServerMessage(error: unknown): string {
	return serverMessage(error);
}

/** The reference Konvo logged a failure under ("7Q4K-2M9X"), when it sent one. */
export function agentErrorRef(error: unknown): string | null {
	const ref = errorBody(error)?.ref;
	return typeof ref === 'string' ? ref : null;
}

/** Why a new chat's checkout couldn't switch branch (SWITCH_FAILED from konvo-agents 0.41.0+). */
export function agentSwitchProblem(error: unknown): AgentSwitchProblem | null {
	const parsed = AgentSwitchProblemSchema.safeParse(errorBody(error)?.switchProblem);
	return parsed.success ? parsed.data : null;
}

export function agentErrorMessage(i18n: I18n, error: unknown): string {
	switch (agentErrorCode(error)) {
		case 'MACHINE_OFFLINE':
			return i18n._(OFFLINE_ERROR);
		case 'MACHINE_READ_ONLY':
			return i18n._(READ_ONLY_ERROR);
		case 'APPROVALS_OFF':
			return i18n._(APPROVALS_OFF_ERROR);
		case 'PROJECT_NOT_SHARED':
			return i18n._(PROJECT_HIDDEN_ERROR);
		case 'MACHINE_TIMEOUT':
			return i18n._(TIMEOUT_ERROR);
		case 'TOOL_OFFLINE':
		case 'BRIDGE_ERROR':
			return i18n._(TOOL_DOWN_ERROR);
		case 'NOT_FOUND':
			return i18n._(NOT_FOUND_ERROR);
		case 'AGENTS_NOT_ENABLED':
			return i18n._(NOT_ENABLED_ERROR);
		case 'GITHUB_NOT_CONNECTED':
			return i18n._(GITHUB_NOT_CONNECTED_ERROR);
		case 'MERGE_NOT_ALLOWED':
			return i18n._(MERGE_NOT_ALLOWED_ERROR);
		case 'NO_MERGERS':
			return i18n._(NO_MERGERS_ERROR);
		case 'ASK_CLOSED':
		// Answered elsewhere, dismissed, or the agent moved on before this answer or dismissal.
		case 'QUESTION_GONE':
			return i18n._(ASK_CLOSED_ERROR);
		case 'QUESTION_CHANGED':
			return i18n._(QUESTION_CHANGED_ERROR);
		case 'ASK_NOT_ALLOWED':
			return i18n._(ASK_NOT_ALLOWED_ERROR);
		case 'MERGE_REFUSED':
			return i18n._(MERGE_REFUSED_ERROR, {reason: serverMessage(error)});
		case 'SWITCH_FAILED':
			return i18n._(SWITCH_FAILED_ERROR);
		case 'CHECKOUT_BUSY':
			return i18n._(CHECKOUT_BUSY_ERROR);
		case 'STASH_FAILED':
			return i18n._(STASH_FAILED_ERROR);
		default:
			// Konvo's or the machine's own explanation beats "Something went wrong".
			return serverMessage(error) || i18n._(GENERIC_ERROR);
	}
}

/** Errors whose words come from the machine (adding or removing a project): its own explanation, else Konvo's. */
export function agentProblemMessage(i18n: I18n, error: unknown): string {
	const own = agentErrorMessage(i18n, error);
	if (own !== i18n._(GENERIC_ERROR)) return own;
	return serverMessage(error) || own;
}

const NOW_SHORT = msg({message: 'now', comment: 'Agents list: a thread updated less than a minute ago.'});
const MINUTES_SHORT = msg({message: '{n}m', comment: 'Agents list: minutes since a thread was updated, e.g. "4m".'});
const HOURS_SHORT = msg({message: '{n}h', comment: 'Agents list: hours since a thread was updated, e.g. "7h".'});
const NOW_LONG = msg({message: 'just now', comment: 'Agents phone list: a thread updated less than a minute ago.'});
const MINUTES_LONG = msg({message: '{n} min', comment: 'Agents phone list: minutes since an update, e.g. "4 min".'});
const HOURS_LONG = msg({message: '{n} h', comment: 'Agents phone list: hours since an update, e.g. "7 h".'});

function dayLabel(i18n: I18n, ago: ShortAgo & {at: number}): string {
	return ago.unit === 'weekday'
		? new Intl.DateTimeFormat(i18n.locale, {weekday: 'short'}).format(ago.at)
		: new Intl.DateTimeFormat(i18n.locale, {day: 'numeric', month: 'short'}).format(ago.at);
}

/** now, 4m, 7h, Sat, 12 Sep */
export function shortTime(i18n: I18n, iso: string | undefined, now: number): string {
	const ago = shortAgo(iso, now);
	if (ago.unit === 'now') return i18n._(NOW_SHORT);
	if (ago.unit === 'minutes') return i18n._(MINUTES_SHORT, {n: ago.n});
	if (ago.unit === 'hours') return i18n._(HOURS_SHORT, {n: ago.n});
	return dayLabel(i18n, ago);
}

/** just now, 4 min, 7 h, Sat */
export function longishTime(i18n: I18n, iso: string | undefined, now: number): string {
	const ago = shortAgo(iso, now);
	if (ago.unit === 'now') return i18n._(NOW_LONG);
	if (ago.unit === 'minutes') return i18n._(MINUTES_LONG, {n: ago.n});
	if (ago.unit === 'hours') return i18n._(HOURS_LONG, {n: ago.n});
	return dayLabel(i18n, ago);
}

const SINCE_TIME = msg({
	message: 'Offline since {time}',
	comment: 'Agents: a machine is offline; {time} is a clock time today, e.g. "13:10".',
});
const SINCE_DAY = msg({
	message: 'Offline since {day}',
	comment: 'Agents: a machine is offline; {day} is a date, e.g. "Sat 3 Oct".',
});
const OFFLINE = msg({message: 'Offline', comment: 'Agents: a machine that has never been seen online.'});

function isToday(at: number, now: number): boolean {
	return new Date(at).toDateString() === new Date(now).toDateString();
}

/** A settled thread's time: the clock time today and yesterday, the weekday this week, else the date. */
export function settledTime(
	i18n: I18n,
	thread: Pick<AgentThreadSummary, 'turnFinishedAt' | 'updatedAt'>,
	now: number,
): string {
	const at = endedAt(thread);
	const startToday = new Date(now).setHours(0, 0, 0, 0);
	const day = 24 * 60 * 60 * 1000;
	if (at >= startToday - day) return clockTime(i18n, at);
	if (at >= startToday - 6 * day) return new Intl.DateTimeFormat(i18n.locale, {weekday: 'short'}).format(at);
	return new Intl.DateTimeFormat(i18n.locale, {day: 'numeric', month: 'short'}).format(at);
}

export function clockTime(i18n: I18n, at: number, hour12?: boolean): string {
	return new Intl.DateTimeFormat(i18n.locale, {hour: '2-digit', minute: '2-digit', hour12}).format(at);
}

const YESTERDAY_AT = msg({
	message: 'Yesterday {time}',
	comment: 'Agents thread: when a prompt, reply or step happened yesterday; {time} is a clock time, e.g. "16:42".',
});

/**
 * When something in a thread happened: the clock time today, "Yesterday 16:42", the weekday this
 * week ("Mon 16:42"), else the date ("9 Oct 16:42", with the year when it isn't this year).
 */
export function itemTime(i18n: I18n, at: number, now: number, hour12?: boolean): string {
	const time = clockTime(i18n, at, hour12);
	const startToday = new Date(now).setHours(0, 0, 0, 0);
	const day = 24 * 60 * 60 * 1000;
	if (at >= startToday) return time;
	if (at >= startToday - day) return i18n._(YESTERDAY_AT, {time});
	if (at >= startToday - 6 * day) {
		return `${new Intl.DateTimeFormat(i18n.locale, {weekday: 'short'}).format(at)} ${time}`;
	}
	const sameYear = new Date(at).getFullYear() === new Date(now).getFullYear();
	const date = new Intl.DateTimeFormat(i18n.locale, {
		day: 'numeric',
		month: 'short',
		year: sameYear ? undefined : 'numeric',
	}).format(at);
	return `${date} ${time}`;
}

/** The full moment, for the hover title: "Sunday 11 October 2026, 16:42:07". */
export function fullTime(i18n: I18n, at: number, hour12?: boolean): string {
	return new Intl.DateTimeFormat(i18n.locale, {
		weekday: 'long',
		day: 'numeric',
		month: 'long',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		second: '2-digit',
		hour12,
	}).format(at);
}

export function offlineSince(i18n: I18n, lastSeenAt: string | null, now: number): string {
	const at = lastSeenAt ? Date.parse(lastSeenAt) : Number.NaN;
	if (!Number.isFinite(at)) return i18n._(OFFLINE);
	if (isToday(at, now)) return i18n._(SINCE_TIME, {time: clockTime(i18n, at)});
	const day = new Intl.DateTimeFormat(i18n.locale, {weekday: 'short', day: 'numeric', month: 'short'}).format(at);
	return i18n._(SINCE_DAY, {day});
}

const LAST_SEEN_TODAY = msg({
	message: 'Last seen today at {time}.',
	comment: 'Agents offline pane: when the machine was last connected, e.g. "Last seen today at 13:10."',
});
const LAST_SEEN_DAY = msg({
	message: 'Last seen {day} at {time}.',
	comment: 'Agents offline pane: when the machine was last connected, e.g. "Last seen Sat 3 Oct at 13:10."',
});

export function lastSeen(i18n: I18n, lastSeenAt: string | null, now: number): string {
	const at = lastSeenAt ? Date.parse(lastSeenAt) : Number.NaN;
	if (!Number.isFinite(at)) return '';
	if (isToday(at, now)) return i18n._(LAST_SEEN_TODAY, {time: clockTime(i18n, at)});
	const day = new Intl.DateTimeFormat(i18n.locale, {weekday: 'short', day: 'numeric', month: 'short'}).format(at);
	return i18n._(LAST_SEEN_DAY, {day, time: clockTime(i18n, at)});
}

const MAY_READ = msg({
	message: 'Read chats only',
	comment: 'Agents machines: what Konvo may do on a machine whose owner only shares for reading.',
});
const MAY_SEND = msg({
	message: 'Read chats, send prompts, answer questions, start and stop threads',
	comment: 'Agents machines: what Konvo may do on a machine whose owner allows sending.',
});
const MAY_APPROVE = msg({
	message: 'Read chats, send prompts, answer questions, start and stop threads, answer approvals',
	comment: 'Agents machines: what Konvo may do on a machine whose owner allows sending and approvals.',
});

export function mayDo(i18n: I18n, limits: AgentLimits): string {
	if (limits.send && limits.approve) return i18n._(MAY_APPROVE);
	if (limits.send) return i18n._(MAY_SEND);
	return i18n._(MAY_READ);
}

export const CONFIG_COMMAND = 'konvo-agents config';

const WANTS_APPROVAL = msg({
	message: 'Wants your approval',
	comment: 'Agents list: second line of a thread whose agent waits for an approval (a command or file change).',
});
const ASKS_QUESTION = msg({
	message: 'Asks you a question',
	comment: 'Agents list: second line of a thread whose agent asked the user a question.',
});
const HAS_PLAN = msg({
	message: 'Has a plan for you to review',
	comment: 'Agents list: second line of a thread whose agent proposed a plan to accept.',
});
const WAITING = msg({
	message: 'Waiting for you',
	comment: 'Agents list: second line of a thread that waits for the user, when the reason is unknown.',
});
const FAILED = msg({
	message: 'Failed',
	comment: 'Agents list: start of the second line of a thread that stopped on an error; the error text follows.',
});
const PAUSED_ON_LIMIT = msg({
	message: 'Paused · {provider} limit',
	comment:
		'Agents list: second line of a chat that stopped because the Claude/Codex plan usage limit ran out. {provider} is e.g. "Claude".',
});
const PAUSED_ON_PLAN = msg({
	message: 'Paused · plan limit',
	comment: 'Agents list: second line of a chat that stopped because its plan usage limit ran out (agent unknown).',
});
const BACK_AT = msg({
	message: 'back {time}',
	comment: 'Agents list: after "Paused · Claude limit", when the limit resets, e.g. "back 13:40".',
});
const LIMIT_RESET = msg({
	message: 'Limit reset',
	comment: 'Agents list: second line of a chat that paused on its plan limit, once the limit has reset.',
});
const SEND_TO_CONTINUE = msg({
	message: 'send a prompt to continue',
	comment: 'Agents list: after "Limit reset" on a chat that paused on its plan limit.',
});
const OUT_OF_CREDITS = msg({
	message: 'Out of credits',
	comment: 'Agents list: second line of a chat that stopped because the agent workspace ran out of credits.',
});

const DAY_MS = 24 * 60 * 60 * 1000;

/** "13:40" today, "Sat 10:00" this week, "Wed 14 Oct" later, in the person's locale. */
export function usageTime(i18n: I18n, iso: string, now: number): string {
	const at = new Date(iso);
	const start = new Date(now);
	start.setHours(0, 0, 0, 0);
	const days = (at.getTime() - start.getTime()) / DAY_MS;
	const options: Intl.DateTimeFormatOptions =
		days < 1
			? {hour: 'numeric', minute: '2-digit'}
			: days < 6
				? {weekday: 'short', hour: 'numeric', minute: '2-digit'}
				: {weekday: 'short', day: 'numeric', month: 'short'};
	return new Intl.DateTimeFormat(i18n.locale, options).format(at);
}

/** Why a thread stopped on its plan and when it goes on (Agents.limitOf). */
export interface LimitInfo {
	kind: 'usage' | 'credits';
	resetsAt: string | null;
	over: boolean;
	providerName: string | null;
}
const WORKING_FOR = msg({
	message: 'Working for {duration}',
	comment:
		'Agents list: second line of a running thread. {duration} is how long its turn has run, e.g. "12m" or "1h 4m".',
});
const JUST_STARTED = msg({
	message: 'Just started',
	comment: 'Agents list: second line of a thread that started working less than a minute ago.',
});
export const IN_BACKGROUND = msg({
	message: 'Working in the background',
	comment:
		'Agents: a thread whose agent finished its turn while its subagents or background jobs still work; they wake it when they finish.',
});
const DONE_WORKED = msg({
	message: 'Done · worked {duration}',
	comment: 'Agents list: second line of a finished thread. {duration} is how long the turn ran, e.g. "6m 12s".',
});
const DONE = msg({
	message: 'Done',
	comment: 'Agents list: second line of a finished thread when its run time is unknown.',
});

/** The second line of a thread row: an emphasised lead (what it waits on, "Failed") and the rest. */
export interface ThreadLine {
	lead?: string;
	rest?: string;
}

/**
 * Where a thread stands, under its title: what it waits on, why it failed, how long it has been
 * working, or how long it worked. Null for a thread that never ran.
 */
export function threadLine(
	i18n: I18n,
	thread: Pick<
		AgentThreadSummary,
		'needsYou' | 'status' | 'waitingOn' | 'error' | 'turnStartedAt' | 'turnFinishedAt' | 'updatedAt'
	> &
		Partial<Pick<AgentThreadSummary, 'background'>>,
	now: number,
	limit?: LimitInfo | null,
): ThreadLine | null {
	if (thread.needsYou) {
		if (thread.waitingOn === 'approval') return {lead: i18n._(WANTS_APPROVAL)};
		if (thread.waitingOn === 'question') return {lead: i18n._(ASKS_QUESTION)};
		if (thread.waitingOn === 'plan') return {lead: i18n._(HAS_PLAN)};
		return {lead: i18n._(WAITING)};
	}
	if (thread.status === 'error' && limit?.kind === 'credits') return {lead: i18n._(OUT_OF_CREDITS)};
	if (thread.status === 'error' && limit?.kind === 'usage') {
		if (limit.over) return {lead: i18n._(LIMIT_RESET), rest: i18n._(SEND_TO_CONTINUE)};
		return {
			lead: limit.providerName ? i18n._(PAUSED_ON_LIMIT, {provider: limit.providerName}) : i18n._(PAUSED_ON_PLAN),
			...(limit.resetsAt ? {rest: i18n._(BACK_AT, {time: usageTime(i18n, limit.resetsAt, now)})} : {}),
		};
	}
	if (thread.status === 'error') return {lead: i18n._(FAILED), ...(thread.error ? {rest: thread.error} : {})};
	if (thread.status === 'running') {
		const seconds = durationSeconds(thread.turnStartedAt, now);
		if (seconds == null) return null;
		return {rest: seconds < 60 ? i18n._(JUST_STARTED) : i18n._(WORKING_FOR, {duration: coarseDuration(i18n, seconds)})};
	}
	if (isBackgroundWorking(thread)) return {rest: i18n._(IN_BACKGROUND)};
	if (thread.status === 'completed' || thread.status === 'interrupted') {
		const seconds = durationSeconds(thread.turnStartedAt, thread.turnFinishedAt);
		return {rest: seconds == null ? i18n._(DONE) : i18n._(DONE_WORKED, {duration: formatDuration(i18n, seconds)})};
	}
	return null;
}

const PULL_CHECKS_FAILED = msg({
	message: 'Checks failed',
	comment: "Agents list: emphasised start of a thread's second line when checks failed on its GitHub pull request.",
});
const PULL_FAILED_REST = msg({
	message: '#{number} · {check}',
	comment:
		'Agents list: rest of the line after "Checks failed". {number} is the pull request number, {check} the first failed check, e.g. "test".',
});
const PULL_CHECKS_CANCELLED = msg({
	message: 'Checks cancelled · #{number}',
	comment:
		"Agents list: a thread's second line when a check on its GitHub pull request was stopped before it finished and none failed.",
});
const PULL_CHECKS_PASSED = msg({
	message: 'Checks passed',
	comment:
		"Agents list: emphasised start of a thread's second line when every check passed on its GitHub pull request.",
});
const PULL_NUMBER = msg({
	message: '#{number}',
	comment: 'Agents list: a GitHub pull request number after "Checks passed", e.g. "#302".',
});
const PULL_CHECKS_RUNNING = msg({
	message: 'Checks running · #{number} · {done} of {total} done',
	comment:
		"Agents list: a thread's second line while checks run on its GitHub pull request. {number} is the pull request number.",
});
const PULL_MERGED = msg({
	message: 'Merged · #{number}',
	comment: "Agents list: a thread's second line once its GitHub pull request is merged.",
});
export const PULL_READY = msg({
	message: 'Ready to merge',
	comment:
		"Agents list: emphasised start of a thread's second line when its pull request passed its checks and the person may merge it.",
});
const PULL_RELEASING = msg({
	message: 'Releasing · #{number}',
	comment: "Agents list: a thread's second line while its merged pull request is being released.",
});
const PULL_LIVE = msg({
	message: 'Live · #{number}',
	comment: "Agents list: a thread's second line once everything its merged pull request set off finished.",
});
const PULL_RELEASE_FAILED = msg({
	message: 'Release failed',
	comment:
		"Agents list: emphasised start of a thread's second line when a release step of its merged pull request failed.",
});
const PULL_RELEASE_CANCELLED = msg({
	message: 'Release cancelled · #{number}',
	comment:
		"Agents list: a thread's second line when a release step of its merged pull request was cancelled and nothing ran after it.",
});
const PULL_CLOSED = msg({
	message: 'Closed · #{number}',
	comment: "Agents list: a thread's second line when its GitHub pull request was closed without merging.",
});
const PULL_OPEN = msg({
	message: 'Pull request #{number}',
	comment: "Agents list: a thread's second line when its GitHub pull request is open and has no checks.",
});

/** The line under a finished thread that has a pull request: how its checks stand, merged or closed. */
export function pullLine(i18n: I18n, pull: AgentPullStatus): (ThreadLine & {failed?: boolean}) | null {
	const number = pull.number;
	const phase = pullPhase(pull);
	if (phase === 'closed') return {rest: i18n._(PULL_CLOSED, {number})};
	if (phase === 'merged') {
		if (pull.release?.live) return {rest: i18n._(PULL_LIVE, {number})};
		if (pull.release?.failed)
			return {lead: i18n._(PULL_RELEASE_FAILED), rest: i18n._(PULL_NUMBER, {number}), failed: true};
		if (pull.release?.cancelled) return {rest: i18n._(PULL_RELEASE_CANCELLED, {number})};
		if (pull.release) return {rest: i18n._(PULL_RELEASING, {number})};
		return {rest: i18n._(PULL_MERGED, {number})};
	}
	if (phase === 'failed') {
		const check = pull.checks.find((c) => c.state === 'failed')?.name ?? '';
		return {lead: i18n._(PULL_CHECKS_FAILED), rest: i18n._(PULL_FAILED_REST, {number, check}), failed: true};
	}
	if (phase === 'cancelled') return {rest: i18n._(PULL_CHECKS_CANCELLED, {number})};
	if (phase === 'passed')
		return {
			lead: i18n._(pull.canMerge && pull.mergeable !== false ? PULL_READY : PULL_CHECKS_PASSED),
			rest: i18n._(PULL_NUMBER, {number}),
		};
	if (phase === 'running') {
		const tally = checksTally(pull.checks);
		return {
			rest: i18n._(PULL_CHECKS_RUNNING, {
				number,
				done: tally.total - tally.running - tally.waiting,
				total: tally.total,
			}),
		};
	}
	return {rest: i18n._(PULL_OPEN, {number})};
}

/** "12m", "1h 4m": a running time that ticks every half minute, so no seconds. */
export function coarseDuration(i18n: I18n, seconds: number): string {
	const minutes = Math.max(1, Math.floor(seconds / 60));
	if (minutes < 60) return i18n._(MINUTES_SHORT, {n: minutes});
	return i18n._(HOURS_MINUTES, {h: Math.floor(minutes / 60), m: minutes % 60});
}

/** "45m", "1h 05m": a total of agent time (the digest's footer), minutes padded under hours. */
export function hoursMinutes(i18n: I18n, ms: number): string {
	const minutes = Math.max(1, Math.round(ms / 60_000));
	if (minutes < 60) return i18n._(MINUTES_SHORT, {n: minutes});
	return i18n._(HOURS_MINUTES, {h: Math.floor(minutes / 60), m: String(minutes % 60).padStart(2, '0')});
}

const SECONDS = msg({message: '{s}s', comment: 'Agents: how long a turn ran, under a minute, e.g. "40s".'});
const MINUTES_SECONDS = msg({
	message: '{m}m {s}s',
	comment: 'Agents: how long a turn ran, e.g. "6m 12s" (minutes and seconds).',
});
const HOURS_MINUTES = msg({message: '{h}h {m}m', comment: 'Agents: how long a turn ran, e.g. "1h 4m".'});

/** "40s", "6m 12s", "1h 4m". */
export function formatDuration(i18n: I18n, seconds: number): string {
	if (seconds < 60) return i18n._(SECONDS, {s: seconds});
	const minutes = Math.floor(seconds / 60);
	if (minutes < 60) return i18n._(MINUTES_SECONDS, {m: minutes, s: seconds % 60});
	return i18n._(HOURS_MINUTES, {h: Math.floor(minutes / 60), m: minutes % 60});
}

const CHATS_TODAY = msg({
	message: '{n, plural, one {# chat today} other {# chats today}}',
	comment: 'Agents Choose project: how many chats in a project were worked on today, e.g. "3 chats today".',
});
const USED_JUST_NOW = msg({
	message: 'just now',
	comment: 'Agents Choose project: a project was last used under a minute ago.',
});
const USED_MINUTES_AGO = msg({
	message: '{n} min ago',
	comment: 'Agents Choose project: minutes since a project was last used, e.g. "5 min ago".',
});
const USED_HOURS_AGO = msg({
	message: '{n}h ago',
	comment: 'Agents Choose project: hours since a project was last used today, e.g. "2h ago".',
});
const USED_YESTERDAY = msg({
	message: 'Yesterday',
	comment: 'Agents Choose project: a project was last used yesterday.',
});

/** When a project was last used: 3 chats today · 2h ago · Yesterday · Mon · 2 Oct */
export function projectLastUsed(
	i18n: I18n,
	use: {last: string | null; today: number} | undefined,
	now: number,
): string {
	if (!use?.last) return '';
	if (use.today > 1) return i18n._(CHATS_TODAY, {n: use.today});
	const at = Date.parse(use.last);
	const minutes = Math.max(0, Math.round((now - at) / 60_000));
	const today = new Date(now);
	today.setHours(0, 0, 0, 0);
	if (at >= today.getTime()) {
		if (minutes < 1) return i18n._(USED_JUST_NOW);
		if (minutes < 60) return i18n._(USED_MINUTES_AGO, {n: minutes});
		return i18n._(USED_HOURS_AGO, {n: Math.round(minutes / 60)});
	}
	if (at >= today.getTime() - 86_400_000) return i18n._(USED_YESTERDAY);
	if (at >= today.getTime() - 6 * 86_400_000)
		return new Intl.DateTimeFormat(i18n.locale, {weekday: 'short'}).format(at);
	return new Intl.DateTimeFormat(i18n.locale, {day: 'numeric', month: 'short'}).format(at);
}

/** The key that, with Enter, sends the other way: ⌘ on Apple devices, Ctrl elsewhere. */
export const modifierKey = () =>
	typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘' : 'Ctrl';
